/**
 * Shared validation utilities for form inputs
 * Intent: Provide centralized, reusable validation logic across the application
 * Ensures consistent validation rules and error messages with explicit success/failure states
 */

// Validation result type with explicit success/failure discrimination
export type ValidationResult = { success: true } | { success: false; error: string };

// Validate if string contains only printable ASCII characters (0x20-0x7E)
export function isPrintable(str: string): boolean {
  return /^[\x20-\x7E]*$/.test(str);
}

// Email validation with standard regex pattern
export function validateEmail(email: string): ValidationResult {
  if (!email.trim()) {
    return { success: false, error: 'Email is required' };
  } else if (!/\S+@\S+\.\S+/.test(email)) {
    return { success: false, error: 'Please enter a valid email address' };
  }
  return { success: true };
}

// Password validation with length and character requirements
export function validatePassword(password: string): ValidationResult {
  if (!password) {
    return { success: false, error: 'Password is required' };
  } else if (password.length < 12) {
    return {
      success: false,
      error: 'Password must be at least 12 characters long',
    };
  } else if (!isPrintable(password)) {
    return {
      success: false,
      error: 'Password must contain only printable characters',
    };
  }
  return { success: true };
}

// Confirm password validation
export function validateConfirmPassword(
  password: string,
  confirmPassword: string,
): ValidationResult {
  if (!confirmPassword) {
    return { success: false, error: 'Please confirm your password' };
  } else if (password !== confirmPassword) {
    return { success: false, error: 'Passwords do not match' };
  }
  return { success: true };
}

// Name validation (first name, last name)
export function validateName(name: string, fieldName: string): ValidationResult {
  if (!name.trim()) {
    return { success: false, error: `${fieldName} is required` };
  } else if (name.trim().length < 2) {
    return {
      success: false,
      error: `${fieldName} must be at least 2 characters long`,
    };
  } else if (name.trim().length > 50) {
    return {
      success: false,
      error: `${fieldName} must be less than 50 characters`,
    };
  } else if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) {
    return {
      success: false,
      error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`,
    };
  }
  return { success: true };
}

// Phone validation (optional field)
export function validatePhone(phone: string): ValidationResult {
  if (!phone.trim()) {
    return { success: true }; // Phone is optional
  } else if (!/^\+?[\d\s\-()]+$/.test(phone.trim())) {
    return { success: false, error: 'Please enter a valid phone number' };
  } else if (phone.replace(/\D/g, '').length < 10) {
    return { success: false, error: 'Phone number must be at least 10 digits' };
  }
  return { success: true };
}
